import type RAPIER_NS from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import type { Physics } from './physics';

/** Gap the character controller keeps from surfaces (m); smaller values let it sink into the ground. */
const SKIN = 0.05;

/** Walker dimensions and feel (metres, seconds). */
export const WALK = {
  radius: 0.3,
  /** Capsule half-height of the straight part: total height 2 × (0.55 + 0.3) = 1.7 m. */
  halfHeight: 0.55,
  eye: 1.62,
  speed: 3.5,
  run: 7.5,
  jump: 5,
  gravity: 18,
  /** Stairs, kerbs and door sills are climbed without jumping. */
  step: 0.4,
  slope: (50 * Math.PI) / 180,
};

/**
 * First-person walker: a kinematic capsule moved by Rapier's character controller (slides along
 * walls, climbs steps up to 40 cm, snaps down stairs, walkable slopes up to 50°), with gravity and jumps
 * integrated here. `feet` is the bottom of the capsule.
 */
export class Walker {
  private readonly body: RAPIER_NS.RigidBody;
  private readonly collider: RAPIER_NS.Collider;
  private readonly ctrl: RAPIER_NS.KinematicCharacterController;
  private vy = 0;
  grounded = false;
  /** Horizontal speed actually achieved on the last step (m/s). */
  speed = 0;
  readonly prev = new THREE.Vector3();
  readonly cur = new THREE.Vector3();

  constructor(
    private readonly phys: Physics,
    feet: THREE.Vector3,
  ) {
    const R = phys.R;
    const c = this.center(feet);
    this.body = phys.world.createRigidBody(R.RigidBodyDesc.kinematicPositionBased().setTranslation(c.x, c.y, c.z));
    this.collider = phys.world.createCollider(R.ColliderDesc.capsule(WALK.halfHeight, WALK.radius), this.body);
    phys.tags.set(this.collider.handle, 'dynamic');
    this.ctrl = phys.world.createCharacterController(SKIN);
    this.ctrl.enableAutostep(WALK.step, 0.15, false);
    this.ctrl.enableSnapToGround(WALK.step);
    this.ctrl.setMaxSlopeClimbAngle(WALK.slope);
    this.ctrl.setMinSlopeSlideAngle(WALK.slope + 0.1);
    this.ctrl.setApplyImpulsesToDynamicBodies(true);
    this.ctrl.setCharacterMass(80);
    this.prev.copy(c);
    this.cur.copy(c);
  }

  private center(feet: THREE.Vector3): THREE.Vector3 {
    return new THREE.Vector3(feet.x, feet.y + WALK.halfHeight + WALK.radius + SKIN, feet.z);
  }

  /** Move instantly (spawn, respawn). */
  teleport(feet: THREE.Vector3): void {
    const c = this.center(feet);
    this.body.setTranslation(c, true);
    this.body.setNextKinematicTranslation(c);
    this.prev.copy(c);
    this.cur.copy(c);
    this.vy = 0;
  }

  /**
   * One fixed step. `wish` = desired horizontal direction (world, length ≤ 1), `run`, `jump` held.
   */
  tick(h: number, wish: THREE.Vector3, run: boolean, jump: boolean): void {
    if (this.grounded && jump) this.vy = WALK.jump;
    this.vy -= WALK.gravity * h;
    const v = run ? WALK.run : WALK.speed;
    const desired = { x: wish.x * v * h, y: this.vy * h, z: wish.z * v * h };
    this.ctrl.computeColliderMovement(this.collider, desired);
    const m = this.ctrl.computedMovement();
    this.grounded = this.ctrl.computedGrounded();
    // Landed, or hit a ceiling: stop the vertical motion.
    if (this.grounded && this.vy < 0) this.vy = -1;
    if (this.vy > 0 && m.y < desired.y * 0.5) this.vy = 0;
    this.prev.copy(this.cur);
    this.cur.set(this.cur.x + m.x, this.cur.y + m.y, this.cur.z + m.z);
    this.body.setNextKinematicTranslation(this.cur);
    this.speed = Math.hypot(m.x, m.z) / h;
  }

  /** Eye position, interpolated between the last two steps by `alpha`. */
  eye(alpha: number, out = new THREE.Vector3()): THREE.Vector3 {
    out.lerpVectors(this.prev, this.cur, alpha);
    out.y += WALK.eye - (WALK.halfHeight + WALK.radius + SKIN);
    return out;
  }

  get feet(): THREE.Vector3 {
    return new THREE.Vector3(this.cur.x, this.cur.y - WALK.halfHeight - WALK.radius - SKIN, this.cur.z);
  }

  dispose(): void {
    this.phys.tags.delete(this.collider.handle);
    this.phys.world.removeCharacterController(this.ctrl);
    this.phys.world.removeRigidBody(this.body);
  }
}
